import React from "react";
import { useTranslation } from "react-i18next";
import { CardFooter } from "reactstrap";
import { CheckboxIcon } from "@aureskonnect/react-ui";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";

import {
    setIsModalConfirmationOpened,
    store as userStore,
} from "@components/VerticalLayout/store";
import {
    deviceStore,
    resetDeviceStore,
    setEditedSelectedRows,
    setIsPrinterModified,
} from "../store";

import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../../../api/hooks";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { associatePeripherals } from "../api";
import { TOAST_OPTIONS } from "../constants";

type AssociationRow = Record<string, unknown>;

type AssociationResult = { error?: boolean } | null;

type PrepheralCardFooterType = {
    dataIsUpdated: boolean | number;
    setIsEdited: (value: boolean) => void;
    setIsDeviceAssociateModalOpened: (value: boolean) => void;
    isDeviceAssociateModalOpened: boolean;
    rowData: AssociationRow[];
    setDataIsUpdated: (value: boolean | number) => void;
};

export default function PrepheralCardFooter({
    dataIsUpdated,
    setIsEdited,
    setIsDeviceAssociateModalOpened,
    isDeviceAssociateModalOpened,
    rowData,
    setDataIsUpdated,
}: PrepheralCardFooterType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const {
        cashManagements,
        monetics,
        printerSelected,
        checkModificationEffect,
        isPrinterModified,
        editedSelectedRows,
    } = useSnapshot(deviceStore);

    const {
        userID,
        franchiseID,
        shopID,
        oneShop,
        isModalConfirmationOpened,
        operatorID,
    } = useSnapshot(userStore);

    async function handleValidateButtonClickEvent() {
        const targetRows: AssociationRow[] =
            editedSelectedRows.length > 1
                ? [...(editedSelectedRows as AssociationRow[])]
                : [rowData[0]];

        const isMulti = targetRows.length > 1;

        if (isMulti && (monetics.length > 0 || cashManagements.length > 0)) {
            toast.warning(
                `${t(
                    "Attention, you cannot associate one payment system with more than one application."
                )}`,
                TOAST_OPTIONS
            );
            return;
        }

        try {
            const results = await Promise.all(
                targetRows.map((row) =>
                    associatePeripherals(fetchGlobal, {
                        userID: userID.toString(),
                        franchiseID,
                        operatorID,
                        oneShop,
                        shopID: shopID.toString(),
                        rowData: [row],
                        printerSelected: [...printerSelected],
                        monetics: [...monetics],
                        cashManagements: [...cashManagements],
                        t,
                        isMulti,
                    })
                )
            );

            resetDeviceStore();
            setEditedSelectedRows([]);

            const allNull = results.every(
                (r): r is null => r === null
            );
            if (allNull) {
                setIsDeviceAssociateModalOpened(!isDeviceAssociateModalOpened);
                return;
            }

            const hasError = results.some(
                (r) => r !== null && (r as AssociationResult)?.error === true
            );
            if (hasError) {
                throw Error("Error while saving selection!");
            }

            setDataIsUpdated(!dataIsUpdated);
            setIsEdited(false);
            setIsDeviceAssociateModalOpened(!isDeviceAssociateModalOpened);

            toast.success(
                `${t("The association of this device was successful")!}`,
                {
                    ...TOAST_OPTIONS,
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
        } catch {
            resetDeviceStore();
            setEditedSelectedRows([]);
            toast.error(`${t("Mistake ! Please try again")}!`, TOAST_OPTIONS);
            setIsDeviceAssociateModalOpened(isDeviceAssociateModalOpened);
        }
    }

    return (
        <div>
            <CardFooter className="Card-footer__clz">
                <div className="justify-content-between d-flex align-items-right  float-right p-2">
                    <CustomSecondaryColorButton
                        outline
                        rounded
                        className="mr-2"
                        variant="light"
                        onClick={() => {
                            if (isPrinterModified) {
                                setDataIsUpdated(!dataIsUpdated);
                            }
                            setIsDeviceAssociateModalOpened(
                                !isDeviceAssociateModalOpened
                            );
                            resetDeviceStore();
                            setEditedSelectedRows([]);
                        }}
                    >
                        <span> {t("Cancel")}</span>
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        variant="primary"
                        onClick={() => {
                            if (checkModificationEffect === true) {
                                handleValidateButtonClickEvent();
                            } else {
                                setIsModalConfirmationOpened(
                                    !isModalConfirmationOpened
                                );
                            }
                            setIsPrinterModified(false);
                        }}
                        className="afa_btn_validatePopup"
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </div>
            </CardFooter>
        </div>
    );
}
